import {
  getCompanySlugFromUrl,
  getCompanySlugFromPath,
  sanitizeCompanySlug,
  ensureSlugInAppPath,
} from './utils/companyDetector';
import { requiresSlugInRoute } from './utils/routeUtils';

export const PWA_INSTALL_DISMISS_KEY = 'hospedapp-pwa-install-dismissed';

const MANIFEST_LINK_ID = 'hospedapp-dynamic-manifest';

function absoluteUrl(path) {
  try {
    return new URL(path, window.location.origin).href;
  } catch (error) {
    return path;
  }
}

function resolveSlug(forcedSlug) {
  return (
    sanitizeCompanySlug(forcedSlug) ||
    sanitizeCompanySlug(getCompanySlugFromPath()) ||
    sanitizeCompanySlug(getCompanySlugFromUrl())
  );
}

export function getPwaInstallUrl(forcedSlug) {
  const slug = resolveSlug(forcedSlug);
  if (requiresSlugInRoute() && slug) {
    return absoluteUrl(`/${slug}/home`);
  }
  return absoluteUrl('/home');
}

function buildManifest(slug) {
  const origin = window.location.origin;
  const startUrl = getPwaInstallUrl(slug);
  // scope debe cubrir TODA la app. Si solo cubre /slug/home, iPhone abre Safari
  // al ir a calendario, huéspedes, etc.
  const id = requiresSlugInRoute() && slug ? `/${slug}/` : '/';

  return {
    id,
    short_name: 'Hospedapp',
    name: 'Hospedapp',
    description: 'Gestión de tu hospedaje',
    lang: 'es',
    start_url: startUrl,
    scope: `${origin}/`,
    display: 'standalone',
    background_color: '#FFFFFF',
    theme_color: '#3A86FF',
    color_scheme_dark: {
      background_color: '#000000',
    },
    icons: [
      {
        src: absoluteUrl('/icons/icon.svg'),
        sizes: '192x192 512x512',
        type: 'image/svg+xml',
        purpose: 'any',
      },
      {
        src: absoluteUrl('/icons/icon-maskable.svg'),
        sizes: '512x512',
        type: 'image/svg+xml',
        purpose: 'maskable',
      },
      {
        src: absoluteUrl('/icons/icon-light-192.png'),
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any',
        color_scheme: 'light',
      },
      {
        src: absoluteUrl('/icons/icon-light-512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any',
        color_scheme: 'light',
      },
      {
        src: absoluteUrl('/icons/icon-dark-192.png'),
        sizes: '192x192',
        type: 'image/png',
        purpose: 'any',
        color_scheme: 'dark',
      },
      {
        src: absoluteUrl('/icons/icon-dark-512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'any',
        color_scheme: 'dark',
      },
      {
        src: absoluteUrl('/icons/icon-light-maskable-512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
        color_scheme: 'light',
      },
      {
        src: absoluteUrl('/icons/icon-dark-maskable-512.png'),
        sizes: '512x512',
        type: 'image/png',
        purpose: 'maskable',
        color_scheme: 'dark',
      },
    ],
  };
}

function removeStaticManifestLinks() {
  document.querySelectorAll('link[rel="manifest"]').forEach((node) => {
    if (node.id === MANIFEST_LINK_ID) return;
    const href = node.getAttribute('href') || '';
    // Nunca dejar el manifest.json estático (start_url "/").
    if (href.includes('manifest.json') && !href.includes('pwa-manifest')) {
      node.parentNode?.removeChild(node);
    }
  });
}

function getOrCreateManifestLink() {
  let link = document.getElementById(MANIFEST_LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    link.id = MANIFEST_LINK_ID;
    document.head.appendChild(link);
  }
  return link;
}

function applyManifestHref(slug, manifest) {
  const link = getOrCreateManifestLink();
  const swHref = slug ? `/pwa-manifest.json?slug=${encodeURIComponent(slug)}` : null;

  if (swHref && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
    link.setAttribute('href', swHref);
    return;
  }

  // data: con start_url+scope correctos. No usar /manifest.json estático.
  link.setAttribute(
    'href',
    `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`
  );

  if (swHref && 'serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(() => {
        const current = document.getElementById(MANIFEST_LINK_ID);
        if (current) current.setAttribute('href', swHref);
      })
      .catch(() => {});
  }
}

export function updatePwaManifest(forcedSlug) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  removeStaticManifestLinks();

  const slug = resolveSlug(forcedSlug);
  if (!slug && requiresSlugInRoute()) {
    return null;
  }

  const manifest = buildManifest(slug);
  applyManifestHref(slug, manifest);
  return manifest.start_url;
}

export function initPwa() {
  if (typeof window === 'undefined') return;

  if (ensureSlugInAppPath()) return;

  updatePwaManifest();

  window.addEventListener('beforeinstallprompt', (event) => {
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(PWA_INSTALL_DISMISS_KEY) === '1';
    } catch (error) {
      dismissed = false;
    }
    if (dismissed) return;

    event.preventDefault();
    window.__hospedappDeferredInstall = event;
    window.dispatchEvent(new Event('hospedapp-install-available'));
  });

  window.addEventListener('appinstalled', () => {
    window.__hospedappDeferredInstall = null;
    window.dispatchEvent(new Event('hospedapp-installed'));
  });

  if (!('serviceWorker' in navigator)) return;

  // También en iPhone: sirve /pwa-manifest.json?slug= con scope amplio.
  navigator.serviceWorker.register('/service-worker.js').catch((error) => {
    console.error('No se pudo preparar el acceso directo:', error);
  });
}
